import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BASE_URL = "https://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}

// Convert dd/MM/yyyy if you want to display nicely; here we keep ISO or blank
function displayDate(d) {
  return s(d);
}

const DailyAttendanceReportPrint = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  // read query params from URL
  const qp = useMemo(() => new URLSearchParams(window.location.search), []);
  const finYearId = s(qp.get("finYearId"));
  const branchType = s(qp.get("branchType")) || "HO";
  const branchId = s(qp.get("branch")) || s(qp.get("branchId")); // support your existing qs keys
  const empcode = s(qp.get("empcode"));
  const empname = s(qp.get("empname"));
  const designation = s(qp.get("designation"));
  const fromDate = s(qp.get("fromDate"));
  const toDate = s(qp.get("toDate"));
  const month = s(qp.get("month"));
  const year = s(qp.get("year"));
  const status = s(qp.get("status"));
  const empType = s(qp.get("empType")); // "working" | "resigned" optional
  const resign = empType === "resigned" ? 0 : 1;

  // you can replace these with values from your company master API later
  const company = {
    name: "Nissi Infotech",
    sub1: "Nissi",
    sub2: "CIVIL CONSTRUCTION",
    addressLine: "3/42-12, Nagappa Colony, Kavundampalayam, Coimbatore - Tamil Nadu.",
    phone: "Phone No : 9003744644",
    footer: "Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com",
  };

  const monthName = useMemo(() => {
    const map = {
      "1": "January",
      "2": "February",
      "3": "March",
      "4": "April",
      "5": "May",
      "6": "June",
      "7": "July",
      "8": "August",
      "9": "September",
      "10": "October",
      "11": "November",
      "12": "December",
    };
    return map[month] || month;
  }, [month]);

  const fetchPrintData = async () => {
    setErr("");
    setLoading(true);
    try {
      if (!finYearId) throw new Error("finYearId missing in query string");

      const res = await axios.get(`${BASE_URL}/api/daily-attendance-report/print-data`, {
        params: {
          finYearId: Number(finYearId),
          branchType,
          branchId,
          empcode,
          empname,
          designation,
          fromDate,
          toDate,
          month,
          year,
          resign,
          status,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
      } else {
        throw new Error(res?.data?.message || "Failed to load print data");
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Print load error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // load data once
  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto print after data loaded
  useEffect(() => {
    if (!loading) {
      // small delay helps render fully before print dialog
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [loading]);

  const onPrint = () => window.print();
  const onClose = () => window.close();

  return (
    <div className="print-root">
      {/* Screen-only top buttons */}
      <div className="no-print topbar">
        <button onClick={onPrint} className="btn">Print</button>
        <button onClick={fetchPrintData} className="btn">Refresh</button>
        <button onClick={onClose} className="btn danger">Close</button>
      </div>

      {/* PAGE */}
      <div className="page">
        {/* Header box (like your screenshot) */}
        <div className="headerBox">
          <div className="headerInner">
            <div className="logoBox">
              {/* Put your real logo path here */}
              <img
                src="/assets/company-logo.png"
                alt="Logo"
                className="logo"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

            <div className="companyBox">
              <div className="companyName">{company.name}</div>
              <div className="companySub">{company.sub1}</div>
              <div className="companySub2">{company.sub2}</div>
              <div className="companyAddr">{company.addressLine}</div>
              <div className="companyAddr">{company.phone}</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="titleRow">
          <div className="titleText">DAILY ATTENDANCE REPORT</div>
        </div>

        {/* Filter summary (optional but useful for print) */}
        <div className="filterBox">
          <div className="filterRow">
            <div><b>Branch</b>: {branchId || "-"}</div>
            <div><b>Employee Type</b>: {empType || "working"}</div>
          </div>
          <div className="filterRow">
            <div><b>Month</b>: {monthName || "-"}</div>
            <div><b>Year</b>: {year || "-"}</div>
          </div>
          <div className="filterRow">
            <div><b>From</b>: {displayDate(fromDate) || "-"}</div>
            <div><b>To</b>: {displayDate(toDate) || "-"}</div>
          </div>
          <div className="filterRow">
            <div><b>Emp Code</b>: {empcode || "-"}</div>
            <div><b>Emp Name</b>: {empname || "-"}</div>
          </div>
          <div className="filterRow">
            <div><b>Designation</b>: {designation || "-"}</div>
            <div><b>Status</b>: {status || "-"}</div>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="msg">Loading...</div>
        ) : err ? (
          <div className="msg error">{err}</div>
        ) : rows.length === 0 ? (
          <div className="msg">No Records Found</div>
        ) : (
          <div className="tableWrap">
            <table className="rptTable">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Designation</th>
                  <th>Gross Salary</th>
                  <th>Acc.No</th>
                  <th>Opening Leave Balance</th>
                  <th>Open Leave Transactions</th>
                  <th>Closing Leave Balance</th>
                  <th>Monthly CL</th>
                  <th>Days Worked</th>
                  <th>Camp</th>
                  <th>Leave Taken</th>
                  <th>Mng Leave 100%</th>
                  <th>Mng Leave 30%</th>
                  <th>Holidays</th>
                  <th>LOP</th>
                  <th>Compulsory LOP</th>
                  <th>Total LOP</th>
                  <th>Total Working Days</th>
                  <th>Branch</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((r, idx) => {
                  const lopVal = Number(r.fld_leavelop ?? r.fld_lop ?? 0) || 0;
                  return (
                    <tr key={(r.fld_Employeeid || idx) + "-" + idx} className={lopVal > 0 ? "lopRow" : ""}>
                      <td className="c">{idx + 1}</td>
                      <td className="c">{r.fld_Employeecode}</td>
                      <td className="c">{r.fld_Employee}</td>
                      <td className="c">{r.fld_designationname}</td>
                      <td className="r">{r.fld_grosssalary}</td>
                      <td className="c">{r.fld_accountno}</td>
                      <td className="r">{r.fld_totleaveob}</td>
                      <td className="r">{r.fld_openleavetransactions}</td>
                      <td className="r">{r.fld_closingleavebal}</td>
                      <td className="r">{r.fld_monthlycausalleave}</td>
                      <td className="r">{r.Daysworked}</td>
                      <td className="r">{r.camp}</td>
                      <td className="r">{r.Leavetaken}</td>
                      <td className="r">{r.manageleave100p}</td>
                      <td className="r">{r.manageleave}</td>
                      <td className="r">{r.Holidays}</td>
                      <td className="r">{r.fld_leavelop}</td>
                      <td className="r">{r.fld_compulsorylop}</td>
                      <td className="r">{r.fld_lop}</td>
                      <td className="r">{r.Workingdays}</td>
                      <td className="c">{r.fld_BranchName}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div className="footer">
          {company.footer}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        .print-root {
          background: #fff;
          padding: 12px;
          font-family: Arial, Helvetica, sans-serif;
          color: #000;
        }
        .topbar {
          display: flex;
          gap: 10px;
          margin-bottom: 12px;
        }
        .btn {
          padding: 10px 14px;
          border: 1px solid #333;
          background: #f5f5f5;
          cursor: pointer;
          border-radius: 6px;
          font-weight: 700;
        }
        .btn:hover { background: #e9e9e9; }
        .btn.danger { background: #ffecec; border-color: #c00; }

        .page {
          width: 100%;
        }

        .headerBox {
          border: 1px solid #000;
          padding: 14px;
          margin-bottom: 10px;
        }
        .headerInner {
          display: grid;
          grid-template-columns: 140px 1fr;
          gap: 12px;
          align-items: center;
        }
        .logoBox {
          height: 90px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .logo {
          max-width: 120px;
          max-height: 90px;
          object-fit: contain;
        }
        .companyBox { text-align: center; }
        .companyName { font-size: 20px; font-weight: 800; }
        .companySub { font-size: 14px; font-weight: 700; margin-top: 2px; }
        .companySub2 { font-size: 14px; font-weight: 700; margin-top: 2px; }
        .companyAddr { font-size: 12px; margin-top: 2px; }

        .titleRow {
          border: 1px solid #000;
          padding: 8px;
          text-align: center;
          margin-bottom: 10px;
        }
        .titleText {
          font-size: 14px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .filterBox {
          border: 1px solid #000;
          padding: 10px;
          margin-bottom: 10px;
          font-size: 12px;
        }
        .filterRow {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin: 2px 0;
        }

        .tableWrap {
          border: 1px solid #000;
          padding: 0;
        }
        .rptTable {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }
        .rptTable th, .rptTable td {
          border: 1px solid #000;
          padding: 6px 6px;
          vertical-align: middle;
        }
        .rptTable th {
          font-weight: 800;
          text-align: center;
        }
        .c { text-align: center; }
        .r { text-align: right; }
        .lopRow { background: #f2d6d6; } /* mild red for print */

        .msg {
          border: 1px solid #000;
          padding: 20px;
          text-align: center;
          font-weight: 700;
        }
        .msg.error { color: #b00020; }

        .footer {
          border: 1px solid #000;
          margin-top: 10px;
          padding: 8px;
          font-size: 11px;
          text-align: center;
        }

        /* Print rules */
        @media print {
          .no-print { display: none !important; }
          .print-root { padding: 0; }
          .page { page-break-after: always; }
        }
      `}</style>
    </div>
  );
};

export default DailyAttendanceReportPrint;